import { z } from 'zod'
import { ReportKpiFilterSchema, ReportKpiGroupingSchema, ReportTimePeriodSchema } from '../kpi'
import { ReportColumnDisplaySchema } from '../column'
import { DateRangeSchema, UTCMillisecondsSchema } from '../../time'
import { CompanyIdSchema, EntityIdSchema, idSchemaV2, isId } from '../../ids'
import { CronFieldsSchema } from '../../cron'
import { AuditPropsSchema } from '../../common'

export const ReportConfigIdSchema = idSchemaV2('ReportConfig')
export type ReportConfigId = z.infer<typeof ReportConfigIdSchema>
export const isReportConfigId = isId(ReportConfigIdSchema)

export const ReportDateRangeUnitSchema = z.enum(['hours', 'days', 'weeks', 'months', 'years'])
export type ReportDateRangeUnit = z.infer<typeof ReportDateRangeUnitSchema>

export const WithReportConfigIdSchema = z.object({
  reportConfigId: ReportConfigIdSchema,
})
export type WithReportConfigId = z.infer<typeof WithReportConfigIdSchema>

export const RelativeDateRangeSchema = z
  .object({
    type: z.literal('relative'),
    value: z.number().int().nonnegative(),
    unit: ReportDateRangeUnitSchema,
  })
  .meta({ description: 'Rolling window from today. value: 0 = today, 1 = last 1 unit, etc.' })
export type RelativeDateRange = z.infer<typeof RelativeDateRangeSchema>

export const REPORT_DATE_RANGE_PRESETS = [
  'all_time',
  'today',
  'yesterday',
  'this_week',
  'this_month',
  'this_quarter',
  'this_year',
  'year_to_date',
  'last_week',
  'last_month',
  'last_quarter',
  'last_year',
] as const
const ReportDateRangePresetSchema = z.enum(REPORT_DATE_RANGE_PRESETS)
export type ReportDateRangePreset = z.infer<typeof ReportDateRangePresetSchema>

export function isReportDateRangePreset(v: unknown): v is ReportDateRangePreset {
  return ReportDateRangePresetSchema.safeParse(v).success
}

export const PresetDateRangeSchema = z
  .object({
    type: z.literal('preset'),
    period: z.enum(REPORT_DATE_RANGE_PRESETS),
  })
  .meta({ description: 'Named calendar period. Boundaries resolved using company timezone at query time.' })
export type PresetDateRange = z.infer<typeof PresetDateRangeSchema>

export const FixedDateRangeSchema = z
  .object({
    type: z.literal('fixed'),
    ...DateRangeSchema.shape,
  })
  .meta({ description: 'Absolute date range. startTime/endTime are resolved to UTC using company timezone.' })
export type FixedDateRange = z.infer<typeof FixedDateRangeSchema>

export const AdvancedDateRangeOffsetSchema = z
  .object({
    direction: z.enum(['minus', 'plus']),
    value: z.number().int().nonnegative(),
    unit: ReportDateRangeUnitSchema,
  })
  .meta({ description: 'Offset from today. e.g. { direction: minus, value: 90, unit: day } = today − 90 days.' })
export type AdvancedDateRangeOffset = z.infer<typeof AdvancedDateRangeOffsetSchema>

export const AdvancedDateRangeSchema = z
  .object({
    type: z.literal('advanced'),
    start: AdvancedDateRangeOffsetSchema.optional(),
    end: AdvancedDateRangeOffsetSchema.optional(),
  })
  .meta({
    description:
      'Independent start/end offsets from today. Omitting start or end means exactly today. Anchor is always today — extendable later.',
  })
export type AdvancedDateRange = z.infer<typeof AdvancedDateRangeSchema>

export const ReportDateRangeSchema = z.discriminatedUnion('type', [
  RelativeDateRangeSchema,
  PresetDateRangeSchema,
  FixedDateRangeSchema,
  AdvancedDateRangeSchema,
])
export type ReportDateRange = z.infer<typeof ReportDateRangeSchema>

export const ReportScheduleSchema = CronFieldsSchema
export type ReportSchedule = z.infer<typeof ReportScheduleSchema>

/**
 * Delivery configurations — discriminated on `type` so new channels can be
 * added without breaking existing configs.
 */
export const ReportEmailDeliverySchema = z.object({
  type: z.literal('email'),
  enabled: z.boolean(),
  recipients: z.array(z.email()),
})
export type ReportEmailDelivery = z.infer<typeof ReportEmailDeliverySchema>

export const ReportDeliveryConfigSchema = z.discriminatedUnion('type', [ReportEmailDeliverySchema])
export type ReportDeliveryConfig = z.infer<typeof ReportDeliveryConfigSchema>

export const ReportRunResultSchema = z.enum(['success', 'error'])
export type ReportRunResult = z.infer<typeof ReportRunResultSchema>

export const ReportRunLogSchema = z.object({
  timestamp: UTCMillisecondsSchema,
  result: ReportRunResultSchema,
  errorReason: z.string().optional(),
})
export type ReportRunLog = z.infer<typeof ReportRunLogSchema>

export const ReportConfigTypeSchema = z.enum(['saved', 'scheduled'])
export type ReportConfigType = z.infer<typeof ReportConfigTypeSchema>

/**
 * Shared fields present on both saved and scheduled configs.
 * The config input mirrors ReportKpiInput so a config can be replayed
 * directly as a report query.
 */
export const ReportConfigBaseSchema = z.object({
  name: z.string().nonempty().meta({ description: 'This will also be used as the filename for scheduled reports' }),
  scopeId: EntityIdSchema.meta({
    description: 'UserId = personal, TeamId = team-wide, CompanyId = company-wide',
  }),
  kpis: z.array(ReportColumnDisplaySchema),
  filter: ReportKpiFilterSchema.optional(),
  grouping: ReportKpiGroupingSchema,
  dateRange: ReportDateRangeSchema,
})
export type ReportConfigBase = z.infer<typeof ReportConfigBaseSchema>

export const REPORT_LAYOUTS = ['table', 'trends'] as const
export const ReportLayoutSchema = z.enum(REPORT_LAYOUTS)
export type ReportLayout = z.infer<typeof ReportLayoutSchema>
export const isReportLayout = (value: string): value is ReportLayout => ReportLayoutSchema.safeParse(value).success

/**
 * A simple named bookmark of a stats view — no schedule, no delivery.
 * Shared per-company.
 *
 * `layout`/`interval` only live here, not on ReportConfigBaseSchema, so a
 * scheduled trends config is unrepresentable: scheduled configs are rendered
 * through /report/kpi by api-report/src/reportConfig/automation.ts, which would
 * email a plain KPI table instead of the trends.
 */
const SavedReportConfigDataShape = {
  configType: z.literal('saved'),
  ...ReportConfigBaseSchema.shape,
  layout: ReportLayoutSchema.optional().meta({
    description: 'Defaults to `table`. `trends` displays selected KPIs by group and requires `interval`.',
  }),
  interval: ReportTimePeriodSchema.optional().meta({
    description: 'Column bucket size for `trends` display. Ignored for `table`.',
  }),
}

const requireTrendsInterval = (input: { layout?: ReportLayout; interval?: unknown }, ctx: z.RefinementCtx): void => {
  if (input.layout !== 'trends' || input.interval !== undefined) return
  ctx.addIssue({
    code: 'custom',
    path: ['interval'],
    message: 'interval is required for trends layout',
  })
}

export const UnsavedSavedReportConfigDataSchema = z
  .object(SavedReportConfigDataShape)
  .superRefine(requireTrendsInterval)
export type UnsavedSavedReportConfigData = z.infer<typeof UnsavedSavedReportConfigDataSchema>

export const SavedReportConfigDataSchema = z
  .object({
    ...AuditPropsSchema.shape,
    ...SavedReportConfigDataShape,
    reportConfigId: ReportConfigIdSchema,
    companyId: CompanyIdSchema,
  })
  .superRefine(requireTrendsInterval)
export type SavedReportConfigData = z.infer<typeof SavedReportConfigDataSchema>

/**
 * An automated report — extends saved with schedule, delivery, and run history.
 */
export const UnsavedScheduledReportConfigDataSchema = z.object({
  configType: z.literal('scheduled'),
  ...ReportConfigBaseSchema.shape,
  schedule: ReportScheduleSchema,
  /** IANA timezone identifier (e.g. 'America/New_York'). Used to resolve the schedule expression. */
  timezone: z.string().optional(),
  delivery: z.array(ReportDeliveryConfigSchema),
})
export type UnsavedScheduledReportConfigData = z.infer<typeof UnsavedScheduledReportConfigDataSchema>

export const ScheduledReportConfigDataSchema = z.object({
  ...AuditPropsSchema.shape,
  ...UnsavedScheduledReportConfigDataSchema.shape,
  reportConfigId: ReportConfigIdSchema,
  companyId: CompanyIdSchema,
  /** Last 50 run logs, most recent first */
  runLog: z.array(ReportRunLogSchema).optional(),
})
export type ScheduledReportConfigData = z.infer<typeof ScheduledReportConfigDataSchema>

/**
 * Union of all report config types, discriminated on `configType`.
 */
export const UnsavedReportConfigDataSchema = z.discriminatedUnion('configType', [
  UnsavedSavedReportConfigDataSchema,
  UnsavedScheduledReportConfigDataSchema,
])
export type UnsavedReportConfigData = z.infer<typeof UnsavedReportConfigDataSchema>

export const ReportConfigDataSchema = z.discriminatedUnion('configType', [
  SavedReportConfigDataSchema,
  ScheduledReportConfigDataSchema,
])
export type ReportConfigData = z.infer<typeof ReportConfigDataSchema>
